import { supabase } from '../lib/supabase';
import { withSession } from '../lib/supabase';
import { getEmployeesFromSalariesGroup } from './masterDataService';
import { triggerWorkflowNotification } from './notificationService';
import { triggerRollbackNotification } from './notificationService';
import { logAuditEvent, AUDIT_ACTIONS, AUDIT_ENTITIES } from './auditLogService';

// ============================================================
// HELPERS
// ============================================================

/** Strip "salary-" prefix so only a valid UUID (or null) reaches UUID columns */
const sanitizeUUID = (id) => {
  if (!id) return null;
  if (typeof id === 'string' && id?.startsWith('salary-')) {
    return id?.replace(/^salary-/, '') || null;
  }
  return id;
};

// ============================================================
// ROW MAPPERS
// ============================================================

const fromTicketRow = (row) => {
  if (!row) return null;
  return {
    id: row?.id,
    ticketNumber: row?.ticket_number,
    calculationId: row?.calculation_id,
    projectName: row?.project_name,
    clientName: row?.client_name,
    projectValue: parseFloat(row?.project_value) || 0,
    totalCost: parseFloat(row?.total_cost) || 0,
    targetMarginPercentage: parseFloat(row?.target_margin_percentage) || 0,
    targetMarginAmount: parseFloat(row?.target_margin_amount) || 0,
    netProfitBeforeIncentives: parseFloat(row?.net_profit_before_incentives) || 0,
    salesPercentage: parseFloat(row?.sales_percentage) || 2,
    salesIncentiveAmount: parseFloat(row?.sales_incentive_amount) || 0,
    projectTeamPercentage: parseFloat(row?.project_team_percentage) || 2,
    projectTeamIncentiveAmount: parseFloat(row?.project_team_incentive_amount) || 0,
    totalIncentives: parseFloat(row?.total_incentives) || 0,
    profitAfterIncentives: parseFloat(row?.profit_after_incentives) || 0,
    // Close Won fields
    dealStatus: row?.deal_status || 'Open',
    dealStatusSource: row?.deal_status_source || null,
    wonDate: row?.won_date,
    wonBy: row?.won_by,
    salesDealOwnerId: row?.sales_deal_owner_id,
    salesDealOwnerName: row?.sales_deal_owner_name_snapshot,
    salesDealOwnerDepartment: row?.sales_deal_owner_department_snapshot,
    salesDealOwnerRole: row?.sales_deal_owner_role_snapshot,
    clientPoReference: row?.client_po_reference,
    finalProjectValue: parseFloat(row?.final_project_value) || 0,
    finalTargetMarginAmount: parseFloat(row?.final_target_margin_amount) || 0,
    dealNotes: row?.deal_notes,
    incentiveTicketCreated: row?.incentive_ticket_created || false,
    // Status fields
    status: row?.status,
    createdBy: row?.created_by,
    creator: row?.creator,
    calculatedBy: row?.calculated_by,
    calculatedAt: row?.calculated_at,
    submittedBy: row?.submitted_by,
    submittedAt: row?.submitted_at,
    approvedBy: row?.approved_by,
    approver: row?.approver,
    approvedAt: row?.approved_at,
    approvalComments: row?.approval_comments,
    financeReviewedBy: row?.finance_reviewed_by,
    financeReviewedAt: row?.finance_reviewed_at,
    paidBy: row?.paid_by,
    paidAt: row?.paid_at,
    paymentReference: row?.payment_reference,
    paymentDate: row?.payment_date,
    financeNotes: row?.finance_notes,
    rejectionReason: row?.rejection_reason,
    holdReason: row?.hold_reason,
    returnComment: row?.return_comment,
    isLocked: row?.is_locked || false,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
    // Calculator Sync fields
    sync_status: row?.sync_status || 'synced',
    last_synced_at: row?.last_synced_at,
    last_synced_by: row?.last_synced_by,
    calculator_project_value: parseFloat(row?.calculator_project_value) || 0,
    calculator_contract_value: parseFloat(row?.calculator_contract_value) || 0,
    calculator_gross_profit: parseFloat(row?.calculator_gross_profit) || 0,
    calculator_incentive_percentage: parseFloat(row?.calculator_incentive_percentage) || 2,
    is_adjustment_ticket: row?.is_adjustment_ticket || false,
    parent_ticket_id: row?.parent_ticket_id,
    adjustment_ticket_id: row?.adjustment_ticket_id,
  };
};

const fromMemberRow = (row) => {
  if (!row) return null;
  return {
    id: row?.id,
    ticketId: row?.ticket_id,
    employeeId: row?.employee_id,
    employeeName: row?.employee_name_snapshot || row?.employee_name,
    role: row?.role_snapshot || row?.role || 'Employee',
    department: row?.department_snapshot || row?.department || 'General',
    memberType: row?.member_type,
    splitMethod: row?.split_method,
    sharePercentage: parseFloat(row?.share_percentage) || 0,
    incentiveAmount: parseFloat(row?.incentive_amount) || 0,
    paymentStatus: row?.payment_status,
    workDescription: row?.work_description,
    addedBy: row?.added_by,
    paidAt: row?.paid_at,
    createdAt: row?.created_at,
  };
};

// ============================================================
// EMPLOYEE POOL — shared pool for ALL member dropdowns
// Both Sales Deal Owner and Project Team Members use this same function
// Source: engineers table (Master Data → Engineers Pool), is_deleted = false
// No filtering by department, role, or type
// ============================================================

export const fetchEmployees = async (filters = {}) => {
  let query = supabase
    ?.from('engineers')
    ?.select('id, name, role, specialization, is_deleted')
    ?.eq('is_deleted', false)
    ?.order('name', { ascending: true });

  if (filters?.search) {
    query = query?.ilike('name', `%${filters?.search}%`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[EmployeePool] Fetch error from engineers table:', error);
    return [];
  }

  // Deduplicate by id just in case
  const seen = new Set();
  const engineerEmployees = (data || [])
    ?.filter(e => {
      if (seen?.has(e?.id)) return false;
      seen?.add(e?.id);
      return true;
    })
    ?.map(e => ({
      id: e?.id,
      employeeCode: null,
      fullName: e?.name || 'Unknown',
      department: e?.specialization || 'Engineering',
      role: e?.role || 'Engineer',
      email: null,
      phone: null,
      isActive: true,
    }));

  // Merge employees from the "Salaries" expense group (if it exists)
  const salaryEmployees = await getEmployeesFromSalariesGroup();

  // Filter salary employees by search if provided
  const filteredSalaryEmployees = filters?.search
    ? salaryEmployees?.filter(e =>
        e?.fullName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        e?.department?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      )
    : salaryEmployees;

  // Merge: engineers first, then salary-group employees (no duplicates by fullName)
  const engineerNames = new Set(engineerEmployees?.map(e => e?.fullName?.toLowerCase()));
  const uniqueSalaryEmployees = filteredSalaryEmployees?.filter(
    e => !engineerNames?.has(e?.fullName?.toLowerCase())
  );

  return [...engineerEmployees, ...uniqueSalaryEmployees];
};

// ============================================================
// TICKET NUMBER
// ============================================================

const getNextTicketNumber = async () => {
  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.select('ticket_number')
    ?.order('created_at', { ascending: false })
    ?.limit(1);

  if (error) throw error;
  if (!data || data?.length === 0) return 'INC-001';

  const last = data?.[0]?.ticket_number || 'INC-000';
  const match = last?.match(/(\d+)$/);
  const num = match ? parseInt(match?.[1], 10) + 1 : 1;
  return `INC-${String(num)?.padStart(3, '0')}`;
};

// ============================================================
// CHECK IF TICKET EXISTS FOR CALCULATION
// ============================================================

export const checkTicketExistsForCalculation = async (calculationId) => {
  if (!calculationId) return null;
  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.select('id, ticket_number, status')
    ?.eq('calculation_id', calculationId)
    ?.maybeSingle();

  if (error) {
    console.error('[IncentiveTickets] Check exists error:', error);
    return null;
  }
  return data ? { id: data?.id, ticketNumber: data?.ticket_number, status: data?.status } : null;
};

// ============================================================
// CLOSE WON — MARK CALCULATION AS CLOSE WON & CREATE TICKET
// ============================================================

export const markAsCloseWonAndCreateTicket = async (calculationId, closeWonData, userId) => {
  console.log('[IncentiveTickets] markAsCloseWonAndCreateTicket called for:', calculationId);

  // 1. Check for duplicate ticket
  const existing = await checkTicketExistsForCalculation(calculationId);
  if (existing) {
    throw new Error(`An incentive ticket already exists for this calculation: ${existing?.ticketNumber}`);
  }

  // 2. Validate required fields
  if (!closeWonData?.salesDealOwnerId) throw new Error('Sales Deal Owner is required.');
  if (!closeWonData?.wonDate) throw new Error('Won Date is required.');
  const finalMargin = parseFloat(closeWonData?.finalTargetMarginAmount) || 0;
  if (finalMargin <= 0) throw new Error('Final Target Margin Amount must be greater than 0.');

  // 3. Update the calculation (estimate) record with Close Won fields
  const estimateUpdate = {
    deal_status: 'Close Won',
    won_date: closeWonData?.wonDate,
    won_by: closeWonData?.wonBy || userId,
    sales_deal_owner_id: sanitizeUUID(closeWonData?.salesDealOwnerId),
    sales_deal_owner_name_snapshot: closeWonData?.salesDealOwnerName,
    sales_deal_owner_department_snapshot: closeWonData?.salesDealOwnerDepartment,
    sales_deal_owner_role_snapshot: closeWonData?.salesDealOwnerRole,
    client_po_reference: closeWonData?.clientPoReference || '',
    final_project_value: parseFloat(closeWonData?.finalProjectValue) || 0,
    final_target_margin_amount: finalMargin,
    deal_notes: closeWonData?.dealNotes || '',
    incentive_ticket_created: true,
  };

  const { error: updateError } = await supabase
    ?.from('estimates')
    ?.update(estimateUpdate)
    ?.eq('id', calculationId);

  if (updateError) {
    console.error('[IncentiveTickets] Failed to update estimate:', updateError);
    throw new Error(`Failed to update calculation: ${updateError?.message}`);
  }

  // 4. Calculate incentive values
  const salesPct = 2;
  const projectPct = 2;
  const salesIncentive = finalMargin * (salesPct / 100);
  const projectIncentive = finalMargin * (projectPct / 100);
  const totalIncentives = salesIncentive + projectIncentive;
  const profitAfterIncentives = finalMargin - totalIncentives;

  // 5. Create the incentive ticket
  const ticketNumber = await getNextTicketNumber();
  const ticketRow = {
    ticket_number: ticketNumber,
    calculation_id: calculationId,
    project_name: closeWonData?.projectName || 'Unnamed Project',
    client_name: closeWonData?.clientName || '',
    project_value: parseFloat(closeWonData?.projectValue) || 0,
    total_cost: parseFloat(closeWonData?.totalCost) || 0,
    target_margin_percentage: parseFloat(closeWonData?.targetMarginPercentage) || 0,
    target_margin_amount: parseFloat(closeWonData?.targetMarginAmount) || 0,
    net_profit_before_incentives: finalMargin,
    final_project_value: parseFloat(closeWonData?.finalProjectValue) || 0,
    final_target_margin_amount: finalMargin,
    sales_percentage: salesPct,
    sales_incentive_amount: salesIncentive,
    project_team_percentage: projectPct,
    project_team_incentive_amount: projectIncentive,
    total_incentives: totalIncentives,
    profit_after_incentives: profitAfterIncentives,
    deal_status: 'Close Won',
    won_date: closeWonData?.wonDate,
    won_by: closeWonData?.wonBy || userId,
    sales_deal_owner_id: sanitizeUUID(closeWonData?.salesDealOwnerId),
    sales_deal_owner_name_snapshot: closeWonData?.salesDealOwnerName,
    sales_deal_owner_department_snapshot: closeWonData?.salesDealOwnerDepartment,
    sales_deal_owner_role_snapshot: closeWonData?.salesDealOwnerRole,
    client_po_reference: closeWonData?.clientPoReference || '',
    deal_notes: closeWonData?.dealNotes || '',
    incentive_ticket_created: true,
    status: 'Pending Team Assignment',
    created_by: userId,
  };

  const { data: ticket, error: ticketError } = await supabase
    ?.from('incentive_tickets')
    ?.insert([ticketRow])
    ?.select()
    ?.single();

  if (ticketError) {
    console.error('[IncentiveTickets] Ticket insert error:', ticketError);
    // Rollback estimate update
    await supabase?.from('estimates')?.update({ deal_status: 'Open', incentive_ticket_created: false })?.eq('id', calculationId);
    throw new Error(`Failed to create incentive ticket: ${ticketError?.message}`);
  }

  // 6. Update estimate with the new ticket ID
  await supabase?.from('estimates')?.update({ incentive_ticket_id: ticket?.id })?.eq('id', calculationId);

  // 7. Insert ALL selected Sales Deal Owners as SALES members (multi-owner support)
  const salesOwners = closeWonData?.salesDealOwners || [
    {
      id: closeWonData?.salesDealOwnerId,
      fullName: closeWonData?.salesDealOwnerName,
      department: closeWonData?.salesDealOwnerDepartment,
      role: closeWonData?.salesDealOwnerRole,
    }
  ];
  const employeeSplits = closeWonData?.employeeSplits || salesOwners?.map(emp => ({
    employee: emp,
    percentage: 100 / salesOwners?.length,
    amount: salesIncentive / salesOwners?.length,
  }));
  const splitMethod = closeWonData?.splitMethod || 'SALES_OWNER_100';

  const memberRows = employeeSplits?.map(({ employee, percentage, amount }) => ({
    ticket_id: ticket?.id,
    employee_id: sanitizeUUID(employee?.id),
    employee_name_snapshot: employee?.fullName || employee?.name || '',
    employee_name: employee?.fullName || employee?.name || '',
    department_snapshot: employee?.department || '',
    department: employee?.department || '',
    role_snapshot: employee?.role || '',
    role: employee?.role || '',
    member_type: 'SALES',
    split_method: splitMethod,
    share_percentage: parseFloat(percentage) || 0,
    incentive_amount: parseFloat(amount) || 0,
    payment_status: 'Pending',
    added_by: userId,
  }));

  const { error: memberError } = await supabase
    ?.from('incentive_ticket_members')
    ?.insert(memberRows);

  if (memberError) {
    console.error('[IncentiveTickets] Member insert error:', memberError);
    // Non-fatal — ticket was created, just log the error
  }

  // 8. Audit log
  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticket?.id,
    action: 'CLOSE_WON_TICKET_CREATED',
    performed_by: userId,
    field_changed: 'status',
    old_value: null,
    new_value: 'Pending Team Assignment',
  }]);

  // 9. Trigger notification for Pending Team Assignment (fire-and-forget)
  triggerWorkflowNotification({
    ticketId: ticket?.id,
    ticketNumber: ticket?.ticket_number || '',
    projectName: ticket?.project_name || '',
    newStatus: 'Pending Team Assignment',
    triggeredByUserId: userId,
    ticketCreatorId: userId,
    salesOwnerUserId: ticket?.sales_deal_owner_id || null,
  })?.catch(err => console.warn('[Notifications] Creation trigger error:', err?.message));

  return fromTicketRow(ticket);
};

// ============================================================
// CREATE TICKET FROM CALCULATION (legacy — kept for generate missing)
// ============================================================

const buildTicketRow = (calcData, ticketNumber, userId) => {
  const targetMarginAmount = parseFloat(calcData?.targetMarginAmount) || 0;
  const salesPct = 2;
  const projectPct = 2;
  const salesIncentive = targetMarginAmount * (salesPct / 100);
  const projectIncentive = targetMarginAmount * (projectPct / 100);
  const totalIncentives = salesIncentive + projectIncentive;
  const profitAfterIncentives = targetMarginAmount - totalIncentives;
  const status = targetMarginAmount > 0 ? 'Pending Team Assignment' : 'On Hold';

  return {
    ticket_number: ticketNumber,
    calculation_id: calcData?.calculationId || null,
    project_name: calcData?.projectName || 'Unnamed Project',
    client_name: calcData?.clientName || '',
    project_value: parseFloat(calcData?.projectValue) || 0,
    total_cost: parseFloat(calcData?.totalCost) || 0,
    target_margin_percentage: parseFloat(calcData?.targetMarginPercentage) || 0,
    target_margin_amount: targetMarginAmount,
    net_profit_before_incentives: parseFloat(calcData?.netProfitBeforeIncentives) || targetMarginAmount,
    sales_percentage: salesPct,
    sales_incentive_amount: salesIncentive,
    project_team_percentage: projectPct,
    project_team_incentive_amount: projectIncentive,
    total_incentives: totalIncentives,
    profit_after_incentives: profitAfterIncentives,
    status,
    created_by: userId,
  };
};

export const createIncentiveTicketFromCalculation = async (calcData, userId) => {
  console.log('[IncentiveTickets] Creating ticket for calculation:', calcData?.calculationId);

  const ticketNumber = await getNextTicketNumber();
  const row = buildTicketRow(calcData, ticketNumber, userId);

  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.insert([row])
    ?.select()
    ?.single();

  if (error) {
    console.error('[IncentiveTickets] Insert error:', error);
    throw new Error(`Failed to create incentive ticket: ${error?.message}`);
  }

  if (data?.id) {
    await supabase?.from('incentive_ticket_audit_logs')?.insert([{
      ticket_id: data?.id,
      action: 'TICKET_CREATED',
      performed_by: userId,
      field_changed: 'status',
      old_value: null,
      new_value: row?.status,
    }]);
  }

  return fromTicketRow(data);
};

// ============================================================
// FETCH TICKETS — with role-based filtering and Close Won enforcement
// ============================================================

export const fetchIncentiveTickets = async (filters = {}) => {
  return withSession(async () => {
  let query = supabase
    ?.from('incentive_tickets')
    ?.select(`*, creator:created_by(id, full_name, email), approver:approved_by(id, full_name, email)`)
    ?.order('created_at', { ascending: false });

  // CRITICAL: Only show Close Won tickets (deal_status_source = Close Won OR deal_status = Close Won)
  // This is the core fix — Project Incentive Tickets must only show Close Won deals
  if (!filters?.includeAllDealStatuses) {
    query = query?.or('deal_status_source.eq.Close Won,deal_status.eq.Close Won');
  }

  if (filters?.status) {
    if (Array.isArray(filters?.status)) {
      query = query?.in('status', filters?.status);
    } else {
      query = query?.eq('status', filters?.status);
    }
  }

  if (filters?.search) {
    query = query?.or(`ticket_number.ilike.%${filters?.search}%,project_name.ilike.%${filters?.search}%,client_name.ilike.%${filters?.search}%`);
  }

  // Role-based filtering
  if (filters?.role === 'finance') {
    query = query?.in('status', ['Approved', 'Ready for Payment', 'Paid']);
  } else if (filters?.role === 'project_manager') {
    query = query?.in('status', ['Pending Team Assignment', 'Calculated', 'Pending Approval']);
  } else if (filters?.role === 'approver') {
    query = query?.in('status', ['Pending Approval', 'Approved', 'Rejected', 'On Hold']);
  }

  // Sales user filter — only see their own deals
  if (filters?.salesOwnerId) {
    query = query?.eq('sales_deal_owner_id', filters?.salesOwnerId);
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch tickets: ${error?.message}`);

  // Filter out invalid tickets in JS (safe even if is_invalid column doesn't exist yet)
  const validTickets = (data || [])?.filter(row => !row?.is_invalid);
  return validTickets?.map(fromTicketRow);
  });
};

export const fetchIncentiveTicketById = async (id) => {
  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.select(`*, creator:created_by(id, full_name, email), approver:approved_by(id, full_name, email)`)
    ?.eq('id', id)
    ?.single();

  if (error) throw error;

  const { data: members } = await supabase
    ?.from('incentive_ticket_members')
    ?.select('*')
    ?.eq('ticket_id', id)
    ?.order('created_at', { ascending: true });

  const { data: approvals } = await supabase
    ?.from('incentive_ticket_approvals')
    ?.select(`*, performer:performed_by(id, full_name, email)`)
    ?.eq('ticket_id', id)
    ?.order('created_at', { ascending: false });

  const { data: auditLogs } = await supabase
    ?.from('incentive_ticket_audit_logs')
    ?.select(`*, performer:performed_by(id, full_name, email)`)
    ?.eq('ticket_id', id)
    ?.order('created_at', { ascending: false })
    ?.limit(50);

  return {
    ...fromTicketRow(data),
    members: (members || [])?.map(fromMemberRow),
    approvals: approvals || [],
    auditLogs: auditLogs || [],
  };
};

// ============================================================
// FETCH ESTIMATES WITH DEAL STATUS (for Saved Calculations view)
// ============================================================

export const fetchEstimatesWithDealStatus = async () => {
  const { data, error } = await supabase
    ?.from('estimates')
    ?.select('id, name, ptc_number, calculations, deal_status, won_date, sales_deal_owner_name_snapshot, client_po_reference, final_project_value, final_target_margin_amount, incentive_ticket_created, incentive_ticket_id, created_at')
    ?.order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch estimates: ${error?.message}`);
  return (data || [])?.map(row => ({
    id: row?.id,
    name: row?.name,
    ptcNumber: row?.ptc_number,
    calculations: row?.calculations,
    dealStatus: row?.deal_status || 'Open',
    wonDate: row?.won_date,
    salesDealOwnerName: row?.sales_deal_owner_name_snapshot,
    clientPoReference: row?.client_po_reference,
    finalProjectValue: parseFloat(row?.final_project_value) || 0,
    finalTargetMarginAmount: parseFloat(row?.final_target_margin_amount) || 0,
    incentiveTicketCreated: row?.incentive_ticket_created || false,
    incentiveTicketId: row?.incentive_ticket_id,
    createdAt: row?.created_at,
  }));
};

// ============================================================
// STATUS TRANSITIONS
// NOTE: Status changes apply ONLY to the incentive_tickets table.
//       The linked calculator (estimates table) is NEVER modified here.
//       Calculator records are only modified during ticket creation (markAsCloseWonAndCreateTicket)
//       or during ticket deletion (deleteIncentiveTicket rollback).
// ============================================================

export const updateTicketStatus = async (ticketId, newStatus, userId, comments = '', extraFields = {}, userObj = null, userProfileObj = null) => {
  const { data: current } = await supabase
    ?.from('incentive_tickets')
    ?.select('status, ticket_number, project_name, created_by, sales_deal_owner_id')
    ?.eq('id', ticketId)
    ?.single();

  const updateData = { status: newStatus, ...extraFields };

  if (newStatus === 'Calculated') {
    updateData.calculated_by = userId;
    updateData.calculated_at = new Date()?.toISOString();
  }
  if (newStatus === 'Pending Approval') {
    updateData.submitted_by = userId;
    updateData.submitted_at = new Date()?.toISOString();
  }
  if (newStatus === 'Approved') {
    updateData.approved_by = userId;
    updateData.approved_at = new Date()?.toISOString();
  }
  if (newStatus === 'Ready for Payment') {
    updateData.finance_reviewed_by = userId;
    updateData.finance_reviewed_at = new Date()?.toISOString();
  }
  if (newStatus === 'Paid') {
    updateData.paid_by = userId;
    updateData.paid_at = new Date()?.toISOString();
    updateData.is_locked = true;
  }

  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.update(updateData)
    ?.eq('id', ticketId)
    ?.select()
    ?.single();

  if (error) throw error;

  // If paid, update all members payment_status
  if (newStatus === 'Paid') {
    await supabase
      ?.from('incentive_ticket_members')
      ?.update({ payment_status: 'Paid', paid_at: new Date()?.toISOString() })
      ?.eq('ticket_id', ticketId);
  }

  await supabase?.from('incentive_ticket_approvals')?.insert([{
    ticket_id: ticketId,
    action: newStatus,
    performed_by: userId,
    comments,
    from_status: current?.status,
    to_status: newStatus,
  }]);

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'STATUS_CHANGED',
    performed_by: userId,
    field_changed: 'status',
    old_value: current?.status,
    new_value: newStatus,
    notes: `Status changed from "${current?.status}" to "${newStatus}". Calculator records unchanged.`,
  }]);

  // Log to global audit trail (fire-and-forget)
  logAuditEvent({
    user: userObj,
    userProfile: userProfileObj,
    action: AUDIT_ACTIONS?.INCENTIVE_TICKET_STATUS_CHANGED,
    entityType: AUDIT_ENTITIES?.INCENTIVE_TICKET,
    entityId: ticketId,
    entityName: current?.ticket_number,
    oldValue: { status: current?.status },
    newValue: { status: newStatus },
    metadata: {
      ticketNumber: current?.ticket_number,
      projectName: current?.project_name,
      comments,
      calculatorUnchanged: true,
    },
  })?.catch(err => console.warn('[AuditLog] Status change log failed:', err?.message));

  // Trigger workflow notification (fire-and-forget)
  triggerWorkflowNotification({
    ticketId,
    ticketNumber: current?.ticket_number || '',
    projectName: current?.project_name || '',
    newStatus,
    triggeredByUserId: userId,
    ticketCreatorId: current?.created_by || null,
    salesOwnerUserId: current?.sales_deal_owner_id || null,
  })?.catch(err => console.warn('[Notifications] Workflow trigger error:', err?.message));

  return fromTicketRow(data);
};

// Validate & Calculate — recalculate incentives and set status to Calculated
export const validateAndCalculate = async (ticketId, userId) => {
  const { data: ticket, error: fetchError } = await supabase
    ?.from('incentive_tickets')
    ?.select('*')
    ?.eq('id', ticketId)
    ?.single();

  if (fetchError) throw fetchError;

  const { data: members } = await supabase
    ?.from('incentive_ticket_members')
    ?.select('*')
    ?.eq('ticket_id', ticketId);

  const salesMembers = (members || [])?.filter(m => m?.member_type === 'SALES');
  const projectMembers = (members || [])?.filter(m => m?.member_type === 'PROJECT_TEAM');

  if (salesMembers?.length === 0) throw new Error('Sales Deal Owner must be selected before calculating.');
  if (projectMembers?.length === 0) throw new Error('At least one Project Team Member must be added before calculating.');

  const netProfit = parseFloat(ticket?.final_target_margin_amount) || parseFloat(ticket?.target_margin_amount) || 0;
  if (netProfit <= 0) throw new Error('Target Margin Amount must be greater than zero.');

  const salesIncentive = netProfit * 0.02;
  const projectIncentive = netProfit * 0.02;
  const totalIncentives = salesIncentive + projectIncentive;
  const profitAfterIncentives = netProfit - totalIncentives;

  // Update ticket financials
  await supabase?.from('incentive_tickets')?.update({
    sales_incentive_amount: salesIncentive,
    project_team_incentive_amount: projectIncentive,
    total_incentives: totalIncentives,
    profit_after_incentives: profitAfterIncentives,
    status: 'Calculated',
    calculated_by: userId,
    calculated_at: new Date()?.toISOString(),
  })?.eq('id', ticketId);

  // Recalculate sales member
  for (const m of salesMembers) {
    await supabase?.from('incentive_ticket_members')?.update({
      incentive_amount: salesIncentive,
      share_percentage: 100,
    })?.eq('id', m?.id);
  }

  // Recalculate project team members equally
  const perPerson = projectMembers?.length > 0 ? projectIncentive / projectMembers?.length : 0;
  const shareEach = projectMembers?.length > 0 ? 100 / projectMembers?.length : 0;
  for (const m of projectMembers) {
    await supabase?.from('incentive_ticket_members')?.update({
      incentive_amount: perPerson,
      share_percentage: shareEach,
    })?.eq('id', m?.id);
  }

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'CALCULATED',
    performed_by: userId,
    field_changed: 'status',
    old_value: ticket?.status,
    new_value: 'Calculated',
  }]);

  // Trigger workflow notification for Calculated stage (fire-and-forget)
  triggerWorkflowNotification({
    ticketId,
    ticketNumber: ticket?.ticket_number || '',
    projectName: ticket?.project_name || '',
    newStatus: 'Calculated',
    triggeredByUserId: userId,
    ticketCreatorId: ticket?.created_by || null,
    salesOwnerUserId: ticket?.sales_deal_owner_id || null,
  })?.catch(err => console.warn('[Notifications] Validate trigger error:', err?.message));

  return fetchIncentiveTicketById(ticketId);
};

// ============================================================
// MEMBERS
// ============================================================

export const setSalesOwner = async (ticketId, employeeData, userId) => {
  // Remove existing SALES members
  await supabase
    ?.from('incentive_ticket_members')
    ?.delete()
    ?.eq('ticket_id', ticketId)
    ?.eq('member_type', 'SALES');

  const { data: ticket } = await supabase
    ?.from('incentive_tickets')
    ?.select('sales_incentive_amount')
    ?.eq('id', ticketId)
    ?.single();

  const salesIncentive = parseFloat(ticket?.sales_incentive_amount) || 0;

  const { data, error } = await supabase
    ?.from('incentive_ticket_members')
    ?.insert([{
      ticket_id: ticketId,
      employee_id: sanitizeUUID(employeeData?.employeeId) || null,
      employee_name_snapshot: employeeData?.employeeName,
      employee_name: employeeData?.employeeName,
      department_snapshot: employeeData?.department || '',
      department: employeeData?.department || '',
      role_snapshot: employeeData?.role || '',
      role: employeeData?.role || '',
      member_type: 'SALES',
      split_method: 'SALES_OWNER_100',
      share_percentage: 100,
      incentive_amount: salesIncentive,
      payment_status: 'Pending',
      added_by: userId,
    }])
    ?.select()
    ?.single();

  if (error) throw error;

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'SALES_OWNER_SET',
    performed_by: userId,
    field_changed: 'sales_owner',
    new_value: employeeData?.employeeName,
  }]);

  return fromMemberRow(data);
};

export const addTicketMember = async (ticketId, memberData, userId) => {
  // Check for duplicate PROJECT_TEAM member using employee_id (primary) or name (fallback)
  const { data: existing } = await supabase
    ?.from('incentive_ticket_members')
    ?.select('id, employee_id, employee_name_snapshot, employee_name')
    ?.eq('ticket_id', ticketId)
    ?.eq('member_type', 'PROJECT_TEAM');

  const isDuplicate = (existing || [])?.some(m => {
    // Prefer employee_id comparison when available
    if (memberData?.employeeId && m?.employee_id) {
      return m?.employee_id === memberData?.employeeId;
    }
    // Fallback to name comparison
    return (m?.employee_name_snapshot || m?.employee_name)?.toLowerCase() === memberData?.employeeName?.toLowerCase();
  });
  if (isDuplicate) throw new Error(`This employee is already added to this ticket.`);

  const { data: ticket } = await supabase
    ?.from('incentive_tickets')
    ?.select('project_team_incentive_amount')
    ?.eq('id', ticketId)
    ?.single();

  const memberCount = (existing?.length || 0) + 1;
  const totalProjectIncentive = parseFloat(ticket?.project_team_incentive_amount) || 0;
  const perPersonAmount = memberCount > 0 ? totalProjectIncentive / memberCount : 0;
  const sharePercentage = memberCount > 0 ? 100 / memberCount : 100;

  // Update existing members
  if (existing?.length > 0) {
    await supabase
      ?.from('incentive_ticket_members')
      ?.update({ incentive_amount: perPersonAmount, share_percentage: sharePercentage })
      ?.eq('ticket_id', ticketId)
      ?.eq('member_type', 'PROJECT_TEAM');
  }

  const { data, error } = await supabase
    ?.from('incentive_ticket_members')
    ?.insert([{
      ticket_id: ticketId,
      employee_id: sanitizeUUID(memberData?.employeeId) || null,
      employee_name_snapshot: memberData?.employeeName,
      employee_name: memberData?.employeeName,
      department_snapshot: memberData?.department || '',
      department: memberData?.department || '',
      role_snapshot: memberData?.role || '',
      role: memberData?.role || '',
      member_type: 'PROJECT_TEAM',
      split_method: 'EQUAL_SPLIT',
      share_percentage: sharePercentage,
      incentive_amount: perPersonAmount,
      payment_status: 'Pending',
      work_description: memberData?.workDescription || '',
      added_by: userId,
    }])
    ?.select()
    ?.single();

  if (error) throw error;

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'MEMBER_ADDED',
    performed_by: userId,
    field_changed: 'members',
    new_value: memberData?.employeeName,
  }]);

  return fromMemberRow(data);
};

export const removeTicketMember = async (ticketId, memberId, userId) => {
  const { error } = await supabase
    ?.from('incentive_ticket_members')
    ?.delete()
    ?.eq('id', memberId);

  if (error) throw error;

  const { data: ticket } = await supabase
    ?.from('incentive_tickets')
    ?.select('project_team_incentive_amount')
    ?.eq('id', ticketId)
    ?.single();

  const { data: remainingMembers } = await supabase
    ?.from('incentive_ticket_members')
    ?.select('id')
    ?.eq('ticket_id', ticketId)
    ?.eq('member_type', 'PROJECT_TEAM');

  const memberCount = remainingMembers?.length || 0;
  if (memberCount > 0) {
    const totalProjectIncentive = parseFloat(ticket?.project_team_incentive_amount) || 0;
    const perPersonAmount = totalProjectIncentive / memberCount;
    const sharePercentage = 100 / memberCount;
    await supabase
      ?.from('incentive_ticket_members')
      ?.update({ incentive_amount: perPersonAmount, share_percentage: sharePercentage })
      ?.eq('ticket_id', ticketId)
      ?.eq('member_type', 'PROJECT_TEAM');
  }

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'MEMBER_REMOVED',
    performed_by: userId,
    field_changed: 'members',
    old_value: memberId,
  }]);

  return true;
};

export const updateMemberPaymentStatus = async (memberId, paymentStatus) => {
  const { error } = await supabase
    ?.from('incentive_ticket_members')
    ?.update({ payment_status: paymentStatus })
    ?.eq('id', memberId);
  if (error) throw error;
  return true;
};

// ============================================================
// GENERATE MISSING TICKETS
// ============================================================

export const generateMissingTickets = async (userId) => {
  // Only generate tickets for Close Won calculations
  const { data: estimates, error: estError } = await supabase
    ?.from('estimates')
    ?.select('id, name, ptc_number, calculations, config, created_at, user_id, deal_status')
    ?.eq('deal_status', 'Close Won')
    ?.order('created_at', { ascending: true });

  if (estError) throw new Error(`Failed to fetch estimates: ${estError?.message}`);

  const { data: existingTickets } = await supabase
    ?.from('incentive_tickets')
    ?.select('calculation_id')
    ?.not('calculation_id', 'is', null);

  const existingCalcIds = new Set((existingTickets || [])?.map(t => t?.calculation_id));
  const missing = (estimates || [])?.filter(e => !existingCalcIds?.has(e?.id));

  if (missing?.length === 0) return { created: 0, skipped: 0, errors: [] };

  let created = 0;
  let skipped = 0;
  const errors = [];

  for (const estimate of missing) {
    try {
      const calcs = estimate?.calculations || {};
      const cfg = estimate?.config || {};
      const sellPrice = parseFloat(calcs?.sellPrice) || 0;
      const totalCost = parseFloat(calcs?.totalCost) || 0;
      const grossProfit = parseFloat(calcs?.grossProfit) || (sellPrice - totalCost);

      const calcData = {
        calculationId: estimate?.id,
        projectName: estimate?.name || 'Unnamed Project',
        clientName: estimate?.ptc_number || '',
        projectValue: sellPrice,
        totalCost: totalCost,
        targetMarginPercentage: parseFloat(cfg?.targetMargin) || 0,
        targetMarginAmount: grossProfit,
        netProfitBeforeIncentives: grossProfit,
      };

      await createIncentiveTicketFromCalculation(calcData, estimate?.user_id || userId);
      created++;
    } catch (err) {
      errors?.push({ estimateId: estimate?.id, error: err?.message });
      skipped++;
    }
  }

  return { created, skipped, errors };
};

// ============================================================
// CREATE TEST TICKET
// ============================================================

export const createTestIncentiveTicket = async (userId) => {
  const calcData = {
    calculationId: null,
    projectName: 'TEST — Demo Project',
    clientName: 'Demo Client',
    projectValue: 50000,
    totalCost: 35000,
    targetMarginPercentage: 30,
    targetMarginAmount: 15000,
    netProfitBeforeIncentives: 15000,
  };
  return createIncentiveTicketFromCalculation(calcData, userId);
};

// ============================================================
// CLEAN INVALID TICKETS (Admin only)
// ============================================================

export const cleanInvalidTickets = async (userId) => {
  // Find tickets linked to non-Close-Won calculations (filter is_invalid in JS to avoid column-not-exist errors)
  const { data: tickets, error: fetchError } = await supabase
    ?.from('incentive_tickets')
    ?.select('id, ticket_number, status, calculation_id, is_invalid')
    ?.neq('status', 'Paid');

  if (fetchError) throw new Error(`Failed to fetch tickets: ${fetchError?.message}`);

  // Filter out already-invalid tickets in JS
  const validTickets = (tickets || [])?.filter(t => !t?.is_invalid);

  let markedInvalid = 0;
  const errors = [];

  for (const ticket of validTickets) {
    if (!ticket?.calculation_id) continue;

    // Check if linked calculation is NOT Close Won
    const { data: estimate } = await supabase
      ?.from('estimates')
      ?.select('deal_status')
      ?.eq('id', ticket?.calculation_id)
      ?.maybeSingle();

    if (!estimate || estimate?.deal_status !== 'Close Won') {
      // Mark as invalid
      const { error: updateError } = await supabase
        ?.from('incentive_tickets')
        ?.update({
          is_invalid: true,
          status: 'Invalid',
          invalidated_at: new Date()?.toISOString(),
          invalidated_by: userId,
        })
        ?.eq('id', ticket?.id);

      if (updateError) {
        errors?.push({ ticketId: ticket?.id, error: updateError?.message });
      } else {
        markedInvalid++;
        // Audit log
        await supabase?.from('incentive_ticket_audit_logs')?.insert([{
          ticket_id: ticket?.id,
          action: 'MARKED_INVALID',
          performed_by: userId,
          field_changed: 'is_invalid',
          old_value: 'false',
          new_value: 'true',
        }])?.catch(() => {});
      }
    }
  }

  return { markedInvalid, errors };
};

// ============================================================
// FINANCE PAYMENT REPORT
// ============================================================

export const fetchFinancePaymentReport = async (filters = {}) => {
  let query = supabase
    ?.from('incentive_tickets')
    ?.select(`*, members:incentive_ticket_members(*)`)
    ?.in('status', ['Approved', 'Ready for Payment', 'Paid'])
    ?.order('created_at', { ascending: false });

  if (filters?.projectName) query = query?.ilike('project_name', `%${filters?.projectName}%`);
  if (filters?.clientName) query = query?.ilike('client_name', `%${filters?.clientName}%`);
  if (filters?.status) query = query?.eq('status', filters?.status);
  if (filters?.dateFrom) query = query?.gte('created_at', filters?.dateFrom);
  if (filters?.dateTo) query = query?.lte('created_at', filters?.dateTo);

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch finance report: ${error?.message}`);

  // Filter out invalid tickets in JS (safe even if is_invalid column doesn't exist yet)
  const validData = (data || [])?.filter(row => !row?.is_invalid);

  const rows = [];
  let totalSalesIncentives = 0;
  let totalProjectIncentives = 0;
  let paidAmount = 0;
  let pendingAmount = 0;
  const employeeSet = new Set();
  const ticketSet = new Set();

  for (const ticket of (validData || [])) {
    ticketSet?.add(ticket?.id);
    for (const member of (ticket?.members || [])) {
      const empName = member?.employee_name_snapshot || member?.employee_name;
      if (filters?.employee && !empName?.toLowerCase()?.includes(filters?.employee?.toLowerCase())) continue;
      if (filters?.department && (member?.department_snapshot || member?.department) !== filters?.department) continue;
      if (filters?.paymentStatus && member?.payment_status !== filters?.paymentStatus) continue;

      employeeSet?.add(empName);
      const amount = parseFloat(member?.incentive_amount) || 0;
      if (member?.member_type === 'SALES') totalSalesIncentives += amount;
      else totalProjectIncentives += amount;

      if (member?.payment_status === 'Paid') paidAmount += amount;
      else pendingAmount += amount;

      rows?.push({
        ticketNumber: ticket?.ticket_number,
        projectName: ticket?.project_name,
        clientName: ticket?.client_name,
        employeeName: empName,
        department: member?.department_snapshot || member?.department || '—',
        role: member?.role_snapshot || member?.role || '—',
        memberType: member?.member_type === 'SALES' ? 'Sales' : 'Project Team',
        incentiveAmount: amount,
        paymentStatus: member?.payment_status,
        paymentReference: ticket?.payment_reference || '—',
        paymentDate: ticket?.payment_date || '—',
        ticketStatus: ticket?.status,
      });
    }
  }

  return {
    rows,
    summary: {
      totalSalesIncentives,
      totalProjectIncentives,
      grandTotal: totalSalesIncentives + totalProjectIncentives,
      numberOfEmployees: employeeSet?.size,
      numberOfTickets: ticketSet?.size,
      paidAmount,
      pendingAmount,
    },
  };
};

// ============================================================
// EMPLOYEE INCENTIVE REPORT
// ============================================================

/**
 * Fetch employee incentive report data.
 *
 * @param {object} filters   - UI filter values (year, month, employee, department, memberType, paymentStatus)
 * @param {object} viewScope - Role-based scope: { scope: 'own'|'team'|'all', employeeId, teamMemberNames }
 */
export const fetchEmployeeIncentiveReport = async (filters = {}, viewScope = { scope: 'all' }) => {
  let query = supabase
    ?.from('incentive_ticket_members')
    ?.select(`*, ticket:ticket_id(id, ticket_number, project_name, client_name, status, payment_reference, payment_date, created_at, paid_at)`)
    ?.order('created_at', { ascending: false });

  // Apply DB-level scope filter for view_own: filter by employee_id if available,
  // otherwise filter by employee_name_snapshot using the user's full name
  if (viewScope?.scope === 'own') {
    if (viewScope?.employeeId) {
      query = query?.eq('employee_id', viewScope?.employeeId);
    } else if (viewScope?.userFullName) {
      // Fallback: filter by name snapshot when no employee_id link exists
      query = query?.ilike('employee_name_snapshot', viewScope?.userFullName?.trim());
    } else {
      // No identifier available — return empty result to prevent data leak
      return [];
    }
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch employee report: ${error?.message}`);

  const employeeMap = {};

  for (const member of (data || [])) {
    const ticket = member?.ticket;
    if (!ticket) continue;

    if (filters?.status && ticket?.status !== filters?.status) continue;
    if (filters?.memberType && member?.member_type !== filters?.memberType) continue;
    if (filters?.paymentStatus && member?.payment_status !== filters?.paymentStatus) continue;

    const empName = member?.employee_name_snapshot || member?.employee_name;

    // Apply view_own scope: secondary in-memory guard
    if (viewScope?.scope === 'own') {
      if (viewScope?.employeeId) {
        // Already filtered at DB level — no further check needed
      } else if (viewScope?.userFullName) {
        // DB already filtered by name; skip records that don't match (case-insensitive safety net)
        if (!empName || empName?.toLowerCase() !== viewScope?.userFullName?.toLowerCase()?.trim()) continue;
      } else {
        // No identifier — skip all records
        continue;
      }
    }

    // Apply view_team scope: only include records for team members
    if (viewScope?.scope === 'team') {
      const teamNames = viewScope?.teamMemberNames || [];
      if (teamNames?.length > 0) {
        const nameMatch = teamNames?.some(n => n?.toLowerCase() === empName?.toLowerCase());
        if (!nameMatch) continue;
      }
    }

    if (filters?.employee && !empName?.toLowerCase()?.includes(filters?.employee?.toLowerCase())) continue;
    const dept = member?.department_snapshot || member?.department || 'General';
    if (filters?.department && dept !== filters?.department) continue;

    if (filters?.year) {
      const year = new Date(ticket?.created_at)?.getFullYear();
      if (year !== parseInt(filters?.year)) continue;
    }
    if (filters?.month) {
      const month = new Date(ticket?.created_at)?.getMonth() + 1;
      if (month !== parseInt(filters?.month)) continue;
    }

    const key = empName;
    if (!employeeMap?.[key]) {
      employeeMap[key] = {
        employeeName: empName,
        department: dept,
        role: member?.role_snapshot || member?.role || '—',
        dealsClosedCount: 0,
        projectsWorkedCount: 0,
        totalSalesIncentive: 0,
        totalProjectIncentive: 0,
        totalIncentive: 0,
        paidAmount: 0,
        pendingAmount: 0,
        tickets: [],
      };
    }

    const emp = employeeMap?.[key];
    const amount = parseFloat(member?.incentive_amount) || 0;

    if (member?.member_type === 'SALES') {
      emp.dealsClosedCount++;
      emp.totalSalesIncentive += amount;
    } else {
      emp.projectsWorkedCount++;
      emp.totalProjectIncentive += amount;
    }

    emp.totalIncentive += amount;
    if (member?.payment_status === 'Paid') emp.paidAmount += amount;
    else emp.pendingAmount += amount;

    emp?.tickets?.push({
      ticketNumber: ticket?.ticket_number,
      projectName: ticket?.project_name,
      clientName: ticket?.client_name,
      memberType: member?.member_type === 'SALES' ? 'Sales' : 'Project Team',
      incentiveAmount: amount,
      status: ticket?.status,
      paymentStatus: member?.payment_status,
      createdDate: ticket?.created_at,
      paidDate: member?.paid_at || ticket?.paid_at,
    });
  }

  return Object.values(employeeMap)?.sort((a, b) => b?.totalIncentive - a?.totalIncentive);
};

// ============================================================
// CALCULATOR SYNC — Live relationship between Calculator & Tickets
// ============================================================

/**
 * Sync an incentive ticket with the latest calculator values.
 * Enforces status-based rules:
 *   Draft / Pending Team Assignment / Calculated → auto-update
 *   Pending Approval / Approved → set sync_status = 'pending_update', request re-approval
 *   Paid → lock ticket, create adjustment ticket if values changed
 */
export const syncTicketWithCalculator = async (calculationId, calculatorData, userId, userFullName = '') => {
  if (!calculationId) return null;

  // Find the linked ticket
  const { data: ticket, error: fetchError } = await supabase
    ?.from('incentive_tickets')
    ?.select('*')
    ?.eq('calculation_id', calculationId)
    ?.maybeSingle();

  if (fetchError || !ticket) return null;

  // If ticket is locked (Paid), create adjustment ticket instead
  if (ticket?.is_locked || ticket?.status === 'Paid') {
    return await _createAdjustmentTicket(ticket, calculatorData, userId, userFullName);
  }

  // Detect which fields changed
  const changes = _detectChanges(ticket, calculatorData);
  if (changes?.length === 0) {
    // No changes — ensure sync_status is 'synced'
    await supabase?.from('incentive_tickets')?.update({
      sync_status: 'synced',
      last_synced_at: new Date()?.toISOString(),
      last_synced_by: userId,
    })?.eq('id', ticket?.id);
    return { ticketId: ticket?.id, status: 'no_changes', changes: [] };
  }

  // Recalculate incentive amounts from new gross profit
  const newGrossProfit = parseFloat(calculatorData?.grossProfit) || parseFloat(ticket?.target_margin_amount) || 0;
  const salesPct = parseFloat(ticket?.sales_percentage) || 2;
  const projectPct = parseFloat(ticket?.project_team_percentage) || 2;
  const newSalesIncentive = newGrossProfit * (salesPct / 100);
  const newProjectIncentive = newGrossProfit * (projectPct / 100);
  const newTotalIncentives = newSalesIncentive + newProjectIncentive;
  const newProfitAfterIncentives = newGrossProfit - newTotalIncentives;

  const autoUpdateStatuses = ['Draft', 'Pending Team Assignment', 'Calculated'];
  const reApprovalStatuses = ['Pending Approval', 'Approved', 'On Hold'];

  if (autoUpdateStatuses?.includes(ticket?.status)) {
    // Auto-update the ticket
    const updatePayload = {
      project_value: parseFloat(calculatorData?.projectValue) || ticket?.project_value,
      total_cost: parseFloat(calculatorData?.totalCost) || ticket?.total_cost,
      target_margin_percentage: parseFloat(calculatorData?.targetMarginPercentage) || ticket?.target_margin_percentage,
      target_margin_amount: newGrossProfit,
      net_profit_before_incentives: newGrossProfit,
      final_target_margin_amount: newGrossProfit,
      final_project_value: parseFloat(calculatorData?.projectValue) || ticket?.final_project_value,
      sales_incentive_amount: newSalesIncentive,
      project_team_incentive_amount: newProjectIncentive,
      total_incentives: newTotalIncentives,
      profit_after_incentives: newProfitAfterIncentives,
      sync_status: 'synced',
      last_synced_at: new Date()?.toISOString(),
      last_synced_by: userId,
      calculator_project_value: parseFloat(calculatorData?.projectValue) || 0,
      calculator_contract_value: parseFloat(calculatorData?.contractValue) || 0,
      calculator_gross_profit: newGrossProfit,
      calculator_incentive_percentage: salesPct,
    };

    // Sync project name and client name if provided by the calculator
    if (calculatorData?.projectName) {
      updatePayload.project_name = calculatorData?.projectName;
    }
    if (calculatorData?.clientName) {
      updatePayload.client_name = calculatorData?.clientName;
    }

    await supabase?.from('incentive_tickets')?.update(updatePayload)?.eq('id', ticket?.id);

    // Recalculate member incentive amounts
    await _recalculateMemberAmounts(ticket?.id, newSalesIncentive, newProjectIncentive);

    // Log each change
    await _logSyncChanges(ticket?.id, calculationId, changes, userId, userFullName, 'auto_update');

    // Audit log
    await supabase?.from('incentive_ticket_audit_logs')?.insert([{
      ticket_id: ticket?.id,
      action: 'CALCULATOR_SYNC_AUTO_UPDATE',
      performed_by: userId,
      field_changed: changes?.map(c => c?.field)?.join(', '),
      old_value: changes?.map(c => `${c?.field}: ${c?.oldValue}`)?.join(' | '),
      new_value: changes?.map(c => `${c?.field}: ${c?.newValue}`)?.join(' | '),
    }]);

    return { ticketId: ticket?.id, status: 'auto_updated', changes };

  } else if (reApprovalStatuses?.includes(ticket?.status)) {
    // Update the actual displayed financial values AND mark as pending_update for re-approval
    const pendingPayload = {
      // Update displayed values so the ticket page shows the latest numbers
      project_value: parseFloat(calculatorData?.projectValue) || ticket?.project_value,
      total_cost: parseFloat(calculatorData?.totalCost) || ticket?.total_cost,
      target_margin_percentage: parseFloat(calculatorData?.targetMarginPercentage) || ticket?.target_margin_percentage,
      target_margin_amount: newGrossProfit,
      net_profit_before_incentives: newGrossProfit,
      final_target_margin_amount: newGrossProfit,
      final_project_value: parseFloat(calculatorData?.projectValue) || ticket?.final_project_value,
      sales_incentive_amount: newSalesIncentive,
      project_team_incentive_amount: newProjectIncentive,
      total_incentives: newTotalIncentives,
      profit_after_incentives: newProfitAfterIncentives,
      // Sync status — requires re-approval
      sync_status: 'pending_update',
      last_synced_at: new Date()?.toISOString(),
      last_synced_by: userId,
      calculator_project_value: parseFloat(calculatorData?.projectValue) || 0,
      calculator_contract_value: parseFloat(calculatorData?.contractValue) || 0,
      calculator_gross_profit: newGrossProfit,
      calculator_incentive_percentage: salesPct,
    };

    // Still sync project/client name even for pending-approval tickets
    if (calculatorData?.projectName) {
      pendingPayload.project_name = calculatorData?.projectName;
    }
    if (calculatorData?.clientName) {
      pendingPayload.client_name = calculatorData?.clientName;
    }

    await supabase?.from('incentive_tickets')?.update(pendingPayload)?.eq('id', ticket?.id);

    // Recalculate member incentive amounts with new values
    await _recalculateMemberAmounts(ticket?.id, newSalesIncentive, newProjectIncentive);

    await _logSyncChanges(ticket?.id, calculationId, changes, userId, userFullName, 'pending_approval');

    await supabase?.from('incentive_ticket_audit_logs')?.insert([{
      ticket_id: ticket?.id,
      action: 'CALCULATOR_SYNC_PENDING_APPROVAL',
      performed_by: userId,
      field_changed: changes?.map(c => c?.field)?.join(', '),
      old_value: changes?.map(c => `${c?.field}: ${c?.oldValue}`)?.join(' | '),
      new_value: changes?.map(c => `${c?.field}: ${c?.newValue}`)?.join(' | '),
    }]);

    return { ticketId: ticket?.id, status: 'pending_approval', changes };
  }

  return { ticketId: ticket?.id, status: 'no_action', changes };
};

/**
 * Apply pending calculator changes to an Approved ticket (re-approval flow).
 * Called when a manager explicitly approves the sync update.
 */
export const applyPendingSyncUpdate = async (ticketId, userId, userFullName = '') => {
  const { data: ticket, error } = await supabase
    ?.from('incentive_tickets')
    ?.select('*')
    ?.eq('id', ticketId)
    ?.single();

  if (error || !ticket) throw new Error('Ticket not found');
  if (ticket?.sync_status !== 'pending_update') throw new Error('No pending sync update for this ticket');

  const newGrossProfit = parseFloat(ticket?.calculator_gross_profit) || parseFloat(ticket?.target_margin_amount) || 0;
  const salesPct = parseFloat(ticket?.sales_percentage) || 2;
  const projectPct = parseFloat(ticket?.project_team_percentage) || 2;
  const newSalesIncentive = newGrossProfit * (salesPct / 100);
  const newProjectIncentive = newGrossProfit * (projectPct / 100);
  const newTotalIncentives = newSalesIncentive + newProjectIncentive;
  const newProfitAfterIncentives = newGrossProfit - newTotalIncentives;

  await supabase?.from('incentive_tickets')?.update({
    project_value: parseFloat(ticket?.calculator_project_value) || ticket?.project_value,
    total_cost: ticket?.total_cost,
    target_margin_amount: newGrossProfit,
    net_profit_before_incentives: newGrossProfit,
    final_target_margin_amount: newGrossProfit,
    final_project_value: parseFloat(ticket?.calculator_project_value) || ticket?.final_project_value,
    sales_incentive_amount: newSalesIncentive,
    project_team_incentive_amount: newProjectIncentive,
    total_incentives: newTotalIncentives,
    profit_after_incentives: newProfitAfterIncentives,
    sync_status: 'synced',
    last_synced_at: new Date()?.toISOString(),
    last_synced_by: userId,
    status: 'Pending Approval',
  })?.eq('id', ticketId);

  await _recalculateMemberAmounts(ticketId, newSalesIncentive, newProjectIncentive);

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'SYNC_UPDATE_APPLIED',
    performed_by: userId,
    field_changed: 'sync_status',
    old_value: 'pending_update',
    new_value: 'synced',
  }]);

  return fetchIncentiveTicketById(ticketId);
};

/**
 * Fetch sync change history for a ticket
 */
export const fetchSyncChanges = async (ticketId) => {
  const { data, error } = await supabase
    ?.from('calculator_sync_changes')
    ?.select('*')
    ?.eq('ticket_id', ticketId)
    ?.order('changed_at', { ascending: false })
    ?.limit(50);

  if (error) return [];
  return data || [];
};

// ============================================================
// INTERNAL SYNC HELPERS
// ============================================================

const _detectChanges = (ticket, calculatorData) => {
  const changes = [];

  const check = (fieldLabel, oldVal, newVal) => {
    const oldNum = parseFloat(oldVal) || 0;
    const newNum = parseFloat(newVal) || 0;
    if (Math.abs(oldNum - newNum) > 0.001) {
      changes?.push({
        field: fieldLabel,
        oldValue: oldNum?.toFixed(3),
        newValue: newNum?.toFixed(3),
      });
    }
  };

  const checkStr = (fieldLabel, oldVal, newVal) => {
    const oldStr = (oldVal || '')?.trim();
    const newStr = (newVal || '')?.trim();
    if (newStr && oldStr !== newStr) {
      changes?.push({ field: fieldLabel, oldValue: oldStr, newValue: newStr });
    }
  };

  check('Project Value', ticket?.project_value, calculatorData?.projectValue);
  check('Contract Value', ticket?.calculator_contract_value || ticket?.project_value, calculatorData?.contractValue || calculatorData?.projectValue);
  check('Gross Profit', ticket?.target_margin_amount, calculatorData?.grossProfit);
  check('Total Cost', ticket?.total_cost, calculatorData?.totalCost);
  check('Target Margin %', ticket?.target_margin_percentage, calculatorData?.targetMarginPercentage);
  checkStr('Project Name', ticket?.project_name, calculatorData?.projectName);
  checkStr('Client Name', ticket?.client_name, calculatorData?.clientName);

  return changes;
};

const _logSyncChanges = async (ticketId, calculationId, changes, userId, userFullName, syncAction) => {
  if (!changes?.length) return;
  const rows = changes?.map(c => ({
    ticket_id: ticketId,
    calculation_id: calculationId,
    field_name: c?.field,
    old_value: c?.oldValue,
    new_value: c?.newValue,
    changed_by: userId,
    changed_by_name: userFullName,
    changed_at: new Date()?.toISOString(),
    sync_action: syncAction,
  }));
  await supabase?.from('calculator_sync_changes')?.insert(rows);
};

const _recalculateMemberAmounts = async (ticketId, newSalesIncentive, newProjectIncentive) => {
  const { data: members } = await supabase
    ?.from('incentive_ticket_members')
    ?.select('id, member_type, share_percentage')
    ?.eq('ticket_id', ticketId);

  if (!members?.length) return;

  const salesMembers = members?.filter(m => m?.member_type === 'SALES');
  const projectMembers = members?.filter(m => m?.member_type === 'PROJECT_TEAM');

  for (const m of salesMembers) {
    const share = parseFloat(m?.share_percentage) || 100;
    await supabase?.from('incentive_ticket_members')?.update({
      incentive_amount: newSalesIncentive * (share / 100),
    })?.eq('id', m?.id);
  }

  const perPerson = projectMembers?.length > 0 ? newProjectIncentive / projectMembers?.length : 0;
  for (const m of projectMembers) {
    await supabase?.from('incentive_ticket_members')?.update({
      incentive_amount: perPerson,
    })?.eq('id', m?.id);
  }
};

const _createAdjustmentTicket = async (originalTicket, calculatorData, userId, userFullName) => {
  const changes = _detectChanges(originalTicket, calculatorData);
  if (changes?.length === 0) return { ticketId: originalTicket?.id, status: 'no_changes', changes: [] };

  const newGrossProfit = parseFloat(calculatorData?.grossProfit) || parseFloat(originalTicket?.target_margin_amount) || 0;
  const salesPct = parseFloat(originalTicket?.sales_percentage) || 2;
  const projectPct = parseFloat(originalTicket?.project_team_percentage) || 2;
  const newSalesIncentive = newGrossProfit * (salesPct / 100);
  const newProjectIncentive = newGrossProfit * (projectPct / 100);
  const newTotalIncentives = newSalesIncentive + newProjectIncentive;
  const newProfitAfterIncentives = newGrossProfit - newTotalIncentives;

  const ticketNumber = await getNextTicketNumber();
  const adjRow = {
    ticket_number: ticketNumber,
    calculation_id: originalTicket?.calculation_id,
    project_name: `[ADJ] ${originalTicket?.project_name}`,
    client_name: originalTicket?.client_name,
    project_value: parseFloat(calculatorData?.projectValue) || originalTicket?.project_value,
    total_cost: parseFloat(calculatorData?.totalCost) || originalTicket?.total_cost,
    target_margin_percentage: parseFloat(calculatorData?.targetMarginPercentage) || originalTicket?.target_margin_percentage,
    target_margin_amount: newGrossProfit,
    net_profit_before_incentives: newGrossProfit,
    final_target_margin_amount: newGrossProfit,
    final_project_value: parseFloat(calculatorData?.projectValue) || originalTicket?.final_project_value,
    sales_percentage: salesPct,
    sales_incentive_amount: newSalesIncentive,
    project_team_percentage: projectPct,
    project_team_incentive_amount: newProjectIncentive,
    total_incentives: newTotalIncentives,
    profit_after_incentives: newProfitAfterIncentives,
    deal_status: 'Close Won',
    won_date: originalTicket?.won_date,
    won_by: originalTicket?.won_by,
    sales_deal_owner_id: originalTicket?.sales_deal_owner_id,
    sales_deal_owner_name_snapshot: originalTicket?.sales_deal_owner_name_snapshot,
    sales_deal_owner_department_snapshot: originalTicket?.sales_deal_owner_department_snapshot,
    sales_deal_owner_role_snapshot: originalTicket?.sales_deal_owner_role_snapshot,
    status: 'Draft',
    created_by: userId,
    is_adjustment_ticket: true,
    parent_ticket_id: originalTicket?.id,
    sync_status: 'synced',
    last_synced_at: new Date()?.toISOString(),
    last_synced_by: userId,
    calculator_project_value: parseFloat(calculatorData?.projectValue) || 0,
    calculator_gross_profit: newGrossProfit,
    calculator_incentive_percentage: salesPct,
  };

  const { data: adjTicket, error: adjError } = await supabase
    ?.from('incentive_tickets')
    ?.insert([adjRow])
    ?.select()
    ?.single();

  if (adjError) throw new Error(`Failed to create adjustment ticket: ${adjError?.message}`);

  // Link adjustment ticket to original
  await supabase?.from('incentive_tickets')?.update({
    adjustment_ticket_id: adjTicket?.id,
  })?.eq('id', originalTicket?.id);

  await _logSyncChanges(adjTicket?.id, originalTicket?.calculation_id, changes, userId, userFullName, 'adjustment_ticket');

  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: originalTicket?.id,
    action: 'ADJUSTMENT_TICKET_CREATED',
    performed_by: userId,
    field_changed: 'adjustment_ticket_id',
    old_value: null,
    new_value: adjTicket?.ticket_number,
  }]);

  return { ticketId: originalTicket?.id, adjustmentTicketId: adjTicket?.id, status: 'adjustment_created', changes };
};

// ============================================================
// DELETE TICKET — rolls back calculator to Open state
// Restricted to Admin / Project Manager roles only
// ============================================================

export const deleteIncentiveTicket = async (ticketId, userId, userObj = null, userProfileObj = null) => {
  // 1. Fetch the ticket so we can rollback the linked calculation
  const { data: ticket, error: fetchError } = await supabase
    ?.from('incentive_tickets')
    ?.select('id, ticket_number, project_name, calculation_id, status, created_by, sales_deal_owner_id')
    ?.eq('id', ticketId)
    ?.single();

  if (fetchError) throw new Error(`Failed to fetch ticket for deletion: ${fetchError?.message}`);
  if (!ticket) throw new Error('Ticket not found.');

  const oldStatus = ticket?.status;
  const ticketNumber = ticket?.ticket_number;
  const projectName = ticket?.project_name;
  const calculationId = ticket?.calculation_id;

  // 2. Write a final audit log entry to incentive_ticket_audit_logs BEFORE deleting
  try {
    await supabase?.from('incentive_ticket_audit_logs')?.insert([{
      ticket_id: ticketId,
      action: 'TICKET_DELETED',
      performed_by: userId,
      field_changed: 'status',
      old_value: oldStatus,
      new_value: 'DELETED',
      notes: `Ticket ${ticketNumber} deleted. Calculator rolled back to Open state.`,
    }]);
  } catch (auditErr) {
    console.warn('[AuditLog] Pre-delete log failed:', auditErr?.message);
  }

  // 3. Delete ticket members first (FK constraint)
  try { await supabase?.from('incentive_ticket_members')?.delete()?.eq('ticket_id', ticketId); } catch (_) {}

  // 4. Delete ticket approvals
  try { await supabase?.from('incentive_ticket_approvals')?.delete()?.eq('ticket_id', ticketId); } catch (_) {}

  // 5. Delete the ticket itself
  const { error: deleteError } = await supabase?.from('incentive_tickets')?.delete()?.eq('id', ticketId);

  if (deleteError) throw new Error(`Failed to delete ticket: ${deleteError?.message}`);

  // 6. ROLLBACK CALCULATOR — reset the linked estimate back to Open state
  //    This does NOT affect any other calculator fields, only the deal/ticket tracking fields
  if (calculationId) {
    const { error: rollbackError } = await supabase?.from('estimates')?.update({
        deal_status: 'Open',
        incentive_ticket_created: false,
        incentive_ticket_id: null,
        // Clear Close Won fields so the calculator is fully reset
        won_date: null,
        won_by: null,
        sales_deal_owner_id: null,
        sales_deal_owner_name_snapshot: null,
        sales_deal_owner_department_snapshot: null,
        sales_deal_owner_role_snapshot: null,
        client_po_reference: null,
        final_project_value: null,
        final_target_margin_amount: null,
        deal_notes: null,
      })?.eq('id', calculationId);

    if (rollbackError) {
      console.error('[IncentiveTickets] Calculator rollback failed:', rollbackError?.message);
      // Non-fatal: ticket is already deleted, log the rollback failure
    }
  }

  // 7. Log to the global audit trail
  await logAuditEvent({
    user: userObj,
    userProfile: userProfileObj,
    action: AUDIT_ACTIONS?.INCENTIVE_TICKET_DELETED,
    entityType: AUDIT_ENTITIES?.INCENTIVE_TICKET,
    entityId: ticketId,
    entityName: ticketNumber,
    oldValue: { status: oldStatus, projectName, calculationId },
    newValue: { deleted: true, calculatorRolledBack: !!calculationId },
    metadata: { ticketNumber, projectName, calculationId, rolledBackToOpen: !!calculationId },
  });

  return { success: true, ticketNumber, calculatorRolledBack: !!calculationId };
};

// ============================================================
// WORKFLOW ROLLBACK
// Moves a ticket back to any previous stage, recalculates,
// logs to audit trail, and sends notifications.
// ============================================================

/**
 * The ordered list of forward workflow stages.
 * Used to validate that the target stage is truly "previous".
 */
const WORKFLOW_STAGE_ORDER = [
  'Pending Team Assignment',
  'Calculated',
  'Pending Approval',
  'Approved',
  'Ready for Payment',
  'Paid',
];

/**
 * Roll back a ticket to a previous workflow stage.
 *
 * @param {string} ticketId
 * @param {string} targetStage  - The stage to roll back TO
 * @param {string} reason       - Mandatory reason entered by the user
 * @param {string} userId       - Auth user id performing the rollback
 * @param {Object} userObj      - Full user object (for global audit log)
 * @param {Object} userProfileObj - User profile (for global audit log)
 */
export const rollbackTicketWorkflow = async (ticketId, targetStage, reason, userId, userObj = null, userProfileObj = null) => {
  if (!reason?.trim()) throw new Error('A rollback reason is required.');

  // 1. Fetch current ticket
  const { data: current, error: fetchError } = await supabase
    ?.from('incentive_tickets')
    ?.select('*')
    ?.eq('id', ticketId)
    ?.single();

  if (fetchError) throw fetchError;

  const currentStageIdx = WORKFLOW_STAGE_ORDER?.indexOf(current?.status);
  const targetStageIdx = WORKFLOW_STAGE_ORDER?.indexOf(targetStage);

  if (targetStageIdx === -1) throw new Error(`Invalid target stage: ${targetStage}`);
  if (currentStageIdx === -1) throw new Error(`Cannot roll back from status: ${current?.status}`);
  if (targetStageIdx >= currentStageIdx) throw new Error('Target stage must be earlier than the current stage.');

  // 2. Build the update payload — clear forward-stage metadata
  const updateData = {
    status: targetStage,
    is_locked: false, // Unlock if it was locked (e.g. Paid)
    rollback_reason: reason?.trim(),
    rollback_by: userId,
    rollback_at: new Date()?.toISOString(),
    rollback_from_status: current?.status,
  };

  // Clear stage-specific metadata for stages being rolled back past
  if (targetStageIdx < WORKFLOW_STAGE_ORDER?.indexOf('Paid')) {
    updateData.paid_by = null;
    updateData.paid_at = null;
    updateData.payment_reference = null;
    updateData.payment_date = null;
    updateData.finance_notes = null;
  }
  if (targetStageIdx < WORKFLOW_STAGE_ORDER?.indexOf('Ready for Payment')) {
    updateData.finance_reviewed_by = null;
    updateData.finance_reviewed_at = null;
  }
  if (targetStageIdx < WORKFLOW_STAGE_ORDER?.indexOf('Approved')) {
    updateData.approved_by = null;
    updateData.approved_at = null;
    updateData.approval_comments = null;
  }
  if (targetStageIdx < WORKFLOW_STAGE_ORDER?.indexOf('Pending Approval')) {
    updateData.submitted_by = null;
    updateData.submitted_at = null;
  }
  if (targetStageIdx < WORKFLOW_STAGE_ORDER?.indexOf('Calculated')) {
    updateData.calculated_by = null;
    updateData.calculated_at = null;
  }

  const { data: updated, error: updateError } = await supabase
    ?.from('incentive_tickets')
    ?.update(updateData)
    ?.eq('id', ticketId)
    ?.select()
    ?.single();

  if (updateError) throw updateError;

  // 3. If rolling back to Paid, reset member payment statuses to Pending
  if (current?.status === 'Paid') {
    await supabase
      ?.from('incentive_ticket_members')
      ?.update({ payment_status: 'Pending', paid_at: null })
      ?.eq('ticket_id', ticketId);
  }

  // 4. If rolling back to Calculated or earlier, recalculate incentives
  if (targetStageIdx <= WORKFLOW_STAGE_ORDER?.indexOf('Calculated')) {
    try {
      const { data: members } = await supabase
        ?.from('incentive_ticket_members')
        ?.select('*')
        ?.eq('ticket_id', ticketId);

      const salesMembers = (members || [])?.filter(m => m?.member_type === 'SALES');
      const projectMembers = (members || [])?.filter(m => m?.member_type === 'PROJECT_TEAM');

      const netProfit = parseFloat(current?.final_target_margin_amount) || parseFloat(current?.target_margin_amount) || 0;
      if (netProfit > 0 && (salesMembers?.length > 0 || projectMembers?.length > 0)) {
        const salesIncentive = netProfit * 0.02;
        const projectIncentive = netProfit * 0.02;
        const totalIncentives = salesIncentive + projectIncentive;
        const profitAfterIncentives = netProfit - totalIncentives;

        await supabase?.from('incentive_tickets')?.update({
          sales_incentive_amount: salesIncentive,
          project_team_incentive_amount: projectIncentive,
          total_incentives: totalIncentives,
          profit_after_incentives: profitAfterIncentives,
        })?.eq('id', ticketId);

        // Recalculate sales member amounts
        for (const m of salesMembers) {
          await supabase?.from('incentive_ticket_members')?.update({
            incentive_amount: salesIncentive,
            share_percentage: 100,
            payment_status: 'Pending',
            paid_at: null,
          })?.eq('id', m?.id);
        }

        // Recalculate project team members equally
        if (projectMembers?.length > 0) {
          const perPerson = projectIncentive / projectMembers?.length;
          const shareEach = 100 / projectMembers?.length;
          for (const m of projectMembers) {
            await supabase?.from('incentive_ticket_members')?.update({
              incentive_amount: perPerson,
              share_percentage: shareEach,
              payment_status: 'Pending',
              paid_at: null,
            })?.eq('id', m?.id);
          }
        }
      }
    } catch (recalcErr) {
      console.warn('[Rollback] Recalculation warning (non-fatal):', recalcErr?.message);
    }
  }

  // 5. Write to incentive_ticket_audit_logs
  await supabase?.from('incentive_ticket_audit_logs')?.insert([{
    ticket_id: ticketId,
    action: 'WORKFLOW_ROLLBACK',
    performed_by: userId,
    field_changed: 'status',
    old_value: current?.status,
    new_value: targetStage,
    notes: `Workflow rolled back from "${current?.status}" to "${targetStage}". Reason: ${reason?.trim()}`,
  }]);

  // 6. Write to global audit_logs
  logAuditEvent({
    user: userObj,
    userProfile: userProfileObj,
    action: AUDIT_ACTIONS?.INCENTIVE_TICKET_STATUS_CHANGED,
    entityType: AUDIT_ENTITIES?.INCENTIVE_TICKET,
    entityId: ticketId,
    entityName: current?.ticket_number,
    oldValue: { status: current?.status },
    newValue: { status: targetStage, rollback_reason: reason?.trim() },
    metadata: {
      ticketNumber: current?.ticket_number,
      projectName: current?.project_name,
      rollbackReason: reason?.trim(),
      rollbackFrom: current?.status,
      rollbackTo: targetStage,
      isRollback: true,
    },
  })?.catch(err => console.warn('[AuditLog] Rollback log failed:', err?.message));

  // 7. Send rollback notifications (fire-and-forget)
  triggerRollbackNotification({
    ticketId,
    ticketNumber: current?.ticket_number || '',
    projectName: current?.project_name || '',
    fromStatus: current?.status,
    toStatus: targetStage,
    reason: reason?.trim(),
    triggeredByUserId: userId,
    ticketCreatorId: current?.created_by || null,
    salesOwnerUserId: current?.sales_deal_owner_id || null,
  })?.catch(err => console.warn('[Notifications] Rollback notification error:', err?.message));

  return fromTicketRow(updated);
};

// ============================================================
// LEGACY EXPORT (kept for backward compat)
// ============================================================

export const fetchIncentiveReports = async () => {
  const { data, error } = await supabase
    ?.from('incentive_tickets')
    ?.select('status, total_incentives, sales_incentive_amount, project_team_incentive_amount');

  if (error) throw error;

  const byStatus = {};
  let totalIncentivesPaid = 0;
  let totalSalesIncentives = 0;
  let totalProjectIncentives = 0;

  for (const t of (data || [])) {
    byStatus[t?.status] = (byStatus?.[t?.status] || 0) + 1;
    if (t?.status === 'Paid') {
      totalIncentivesPaid += parseFloat(t?.total_incentives) || 0;
      totalSalesIncentives += parseFloat(t?.sales_incentive_amount) || 0;
      totalProjectIncentives += parseFloat(t?.project_team_incentive_amount) || 0;
    }
  }

  return {
    total: data?.length || 0,
    byStatus,
    totalIncentivesPaid,
    totalSalesIncentives,
    totalProjectIncentives,
  };
};